import React, { Dispatch, SetStateAction } from 'react';
import { ExerciseOrSuperset, RoutineExercise, RoutineExerciseHashMap } from '../../../types/Fisiofit/routine';
import { EXERCISE_TYPE, ExerciseShort } from '../../../types/Fisiofit/exercise';
import { isSuperset } from '../../../utils/supersetUtils';
import { RoutineForm } from '../validators';
import { buildExerciseValues, findFirstExerciseWithId, updateDuplicateExercises } from '../utils/exercises';

interface UseRoutineExercisesOptions {
  form: RoutineForm;
  setForm: Dispatch<SetStateAction<RoutineForm>>;
  setFormFields: (fields: DeepPartial<RoutineForm>) => void;
  organizationExercises: ExerciseShort[] | undefined;
  exercisePreloadValues: RoutineExerciseHashMap | undefined;
  expandedExerciseIds: Record<string, boolean>;
  setExpandedExerciseIds: Dispatch<SetStateAction<Record<string, boolean>>>;
}

export const useRoutineExercises = ({
  form,
  setForm,
  setFormFields,
  organizationExercises,
  exercisePreloadValues,
  expandedExerciseIds,
  setExpandedExerciseIds,
}: UseRoutineExercisesOptions) => {
  // Función para alternar la expansión de un ejercicio
  const toggleExerciseExpand = (dayIndex: number, exerciseIndex: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const exerciseId = `${dayIndex}-${exerciseIndex}`;
    const isExpanded = expandedExerciseIds[exerciseId] || false;
    const newExpandedState = !isExpanded;

    // Actualizar solo el ejercicio específico, manteniendo el estado del resto
    setExpandedExerciseIds((prev) => {
      const newState = { ...prev };
      if (newExpandedState) {
        newState[exerciseId] = true;
      } else {
        delete newState[exerciseId];
      }
      return newState;
    });
  };

  // Función para actualizar un ejercicio
  const updateExercise = (
    dayIndex: number,
    exerciseIndex: number,
    updatedExercise: Partial<RoutineExercise>,
    e?: React.SyntheticEvent,
  ) => {
    if (e) {
      e.stopPropagation();
    }

    // Verificar que no estemos tratando de actualizar una superserie directamente
    const exerciseOrSuperset = form.exerciseDays[dayIndex].exercises[exerciseIndex];
    if (isSuperset(exerciseOrSuperset)) {
      console.warn('No se puede actualizar una superserie directamente. Use updateSupersetExercise.');
      return;
    }

    // Crear una copia profunda del array de días para garantizar detección de cambios
    const updatedDays = JSON.parse(JSON.stringify(form.exerciseDays));

    // Actualizar el ejercicio específico
    const currentExercise = updatedDays[dayIndex].exercises[exerciseIndex] as RoutineExercise;
    const updatedExerciseWithId = { ...currentExercise, ...updatedExercise };

    // Actualizar todos los ejercicios duplicados en el mismo día
    updatedDays[dayIndex].exercises = updateDuplicateExercises(
      updatedDays[dayIndex].exercises,
      updatedExerciseWithId,
      exerciseIndex,
    );

    // Actualizar el estado del formulario con los días actualizados
    setFormFields({ exerciseDays: updatedDays });
  };

  // Función para eliminar un ejercicio
  const removeExercise = (dayIndex: number, exerciseIndex: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      // Verificar consistencia
      if (!form.exerciseDays[dayIndex] || !form.exerciseDays[dayIndex].exercises[exerciseIndex]) {
        return;
      }

      // Crear una copia profunda para garantizar detección de cambios
      const updatedDays = form.exerciseDays.map((day) => ({ ...day, exercises: [...day.exercises] }));

      // Eliminamos el ejercicio (puede ser individual o superserie)
      updatedDays[dayIndex].exercises.splice(exerciseIndex, 1);

      // Actualizamos el estado completo del formulario
      const newForm = {
        ...form,
        exerciseDays: updatedDays,
      };
      setForm(newForm);
    } catch (error) {
      // Error al eliminar ejercicio
    }
  };

  // Función para añadir un nuevo ejercicio a un día
  const addExerciseToDay = (dayIndex: number) => {
    try {
      // Verificar consistencia
      if (dayIndex < 0 || dayIndex >= form.exerciseDays.length) {
        return;
      }

      // Crear una copia profunda para garantizar detección de cambios
      const updatedDays = form.exerciseDays.map((day) => ({ ...day, exercises: [...day.exercises] }));

      // Crear un nuevo ejercicio vacío
      const newExercise: RoutineExercise = {
        exerciseId: '',
        name: '',
        exerciseType: EXERCISE_TYPE.REPS,
      };

      // Añadirlo al día correspondiente como ejercicio individual (no superserie)
      updatedDays[dayIndex].exercises.push(newExercise as ExerciseOrSuperset);

      // Actualizamos el estado completo del formulario
      const newForm = {
        ...form,
        exerciseDays: updatedDays,
      };
      setForm(newForm);

      // Expandir automáticamente el nuevo ejercicio
      const exerciseIndex = updatedDays[dayIndex].exercises.length - 1;
      toggleExerciseExpand(dayIndex, exerciseIndex);
    } catch (error) {
      // Error al añadir ejercicio
    }
  };

  // Función para manejar la selección de un ejercicio
  const handleExerciseSelect = (dayIndex: number, exerciseIndex: number, selectedId: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Verificar que no estemos seleccionando para una superserie
    const exerciseOrSuperset = form.exerciseDays[dayIndex].exercises[exerciseIndex];
    if (isSuperset(exerciseOrSuperset)) {
      console.warn('Use handleSupersetExerciseSelect para superseries.');
      return;
    }

    const selectedExercise = organizationExercises?.find((e) => e.id === selectedId);
    const preloadValues = exercisePreloadValues?.[selectedId];

    if (selectedExercise) {
      // Buscar si ya existe un ejercicio con el mismo ID en este día
      const existingExercise = findFirstExerciseWithId(form.exerciseDays[dayIndex].exercises, selectedId);

      // Verificar si es realmente un duplicado (no el mismo ejercicio)
      const currentExercise = exerciseOrSuperset as RoutineExercise;
      const isDuplicate = existingExercise && existingExercise !== currentExercise;

      const updatedValues = buildExerciseValues(selectedExercise, preloadValues, isDuplicate ? existingExercise : null);

      updateExercise(dayIndex, exerciseIndex, updatedValues);
    }
  };

  return { toggleExerciseExpand, updateExercise, removeExercise, addExerciseToDay, handleExerciseSelect };
};
